/**
 * Modale « Renommer l'équipe » — nom et description. Sortie de `TeamDetailPage` pour que la
 * page ne porte plus aucun `BTN_PRIMARY` : une modale est un écran à part, elle garde son
 * propre bouton de validation principal (règle de cohérence 1.3.0 d'`@exidis/ui`, appliquée
 * par écran), comme `CreateTeamDialog`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { appAlert } from './service.js';
import { Modal } from '../components/Modal.jsx';
import {
  BTN_GHOST_SM,
  BTN_PRIMARY_SM,
  FORM_FIELD,
  FORM_LABEL,
  INPUT,
  TEXTAREA,
} from '../ui/theme.js';

export function RenameTeamDialog({ teamId, team, onClose, onRenamed }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: team.name, description: team.description ?? '' });
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/teams/${teamId}`, {
        name: form.name.trim(),
        description: form.description.trim() || null,
      });
      await onRenamed();
    } catch (error) {
      await appAlert(t('teams.renameFailed', { message: error.message }), {
        title: t('teams.renameModalTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t('teams.renameModalTitle')} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className={FORM_FIELD}>
          <span className={FORM_LABEL}>{t('teams.renameNameLabel')}</span>
          <input
            className={INPUT}
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            required
          />
        </label>
        <label className={FORM_FIELD}>
          <span className={FORM_LABEL}>{t('teams.renameDescriptionLabel')}</span>
          <textarea
            className={TEXTAREA}
            rows={3}
            value={form.description}
            onChange={(event) =>
              setForm((current) => ({ ...current, description: event.target.value }))
            }
          />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className={BTN_GHOST_SM} onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button type="submit" className={BTN_PRIMARY_SM} disabled={busy}>
            {t('teams.renameSave')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
